import { api } from "../api";
import { openModal, closeModal, modalBody } from "../components/modal";
import { confirmDialog } from "../components/confirm-dialog";
import { canWrite, writeOnly, readOnlyNotice, lockInputs } from "../utils/permissions";
import { showToast } from "../utils/toast";
import { RHYTHM_OPTIONS, stageLabel } from "../utils/stage";
import { renderCurriculumEditor } from "../components/lehrplan-editor";
import type { CourseModel, TrainingPrograms, TrainingProgram } from "../types";
import { esc } from "../utils/html";
import { Icons } from "../utils/icons";

/**
 * Ausbildungsmodelle: Takt, Anzahl und Benennung der Stufen, und der Lehrplan.
 *
 * Eine Klasse bekommt ein Modell zugewiesen und bringt selbst nur den Kursbeginn mit.
 * Daraus errechnet sich die laufende Stufe; der Lehrplan des Modells sagt, welche Fächer
 * in welcher Stufe mit wie vielen UE laufen. Die Seite zeigt erst die Liste, ein Klick
 * öffnet ein Modell mit seinem Lehrplan. Das gewählte Modell steht im URL-Query
 * (…&bereich=ausbildungsmodelle&modell=<id>), damit Zurück und Deep-Links funktionieren.
 */

let models: CourseModel[] = [];
let programs: TrainingPrograms = { standardGroups: [], customGroups: [] };
let currentEl: HTMLElement | null = null;
let rootEl: HTMLElement | null = null;

function fmtUe(n: number): string {
  return (Math.round(n * 100) / 100).toLocaleString("de-DE");
}

/** Gewähltes Modell aus dem URL-Query; null = Liste. */
function modelFromUrl(): number | null {
  const query = window.location.hash.split("?")[1] || "";
  const id = Number(new URLSearchParams(query).get("modell"));
  return Number.isInteger(id) && id > 0 ? id : null;
}

function navigate(modelId: number | null) {
  const base = "#/einstellungen?tab=stundenplan&bereich=ausbildungsmodelle";
  history.pushState(null, "", modelId ? `${base}&modell=${modelId}` : base);
  if (rootEl) show(rootEl);
}

function lehrplanSummary(m: CourseModel): string {
  const c = m.curriculum;
  if (!c || c.subjectCount === 0) return `<span class="text-muted">noch leer</span>`;
  return `${c.subjectCount} Fächer${c.schuleUe !== null ? ` · ${fmtUe(c.schuleUe)} UE` : ""}`;
}

function allPrograms(): TrainingProgram[] {
  return [...programs.standardGroups, ...programs.customGroups];
}

/** Vorschau der Stufen, etwa "Unterkurs, Mittelkurs, Oberkurs". */
function stagePreview(m: Pick<CourseModel, "stageNoun" | "stageLabels" | "stageCount">): string {
  return Array.from({ length: m.stageCount }, (_, i) => stageLabel(m, i + 1)).join(", ");
}

function rhythmText(months: number): string {
  const known = RHYTHM_OPTIONS.find(o => o.months === months);
  return known ? known.label : `Alle ${months} Monate`;
}

function renderTable() {
  const tbody = currentEl?.querySelector("tbody");
  if (!tbody) return;

  if (models.length === 0) {
    tbody.innerHTML = `<tr><td colspan="6" class="empty-state">Noch keine Ausbildungsmodelle angelegt</td></tr>`;
    return;
  }

  tbody.innerHTML = models.map(m => `
    <tr class="am-row" data-open="${m.id}">
      <td style="font-weight:500">${esc(m.name)}${m.active ? "" : ` <span class="badge badge-gray">inaktiv</span>`}</td>
      <td class="text-muted">${esc(m.customGroupName || m.departmentGroupName || "Alle")}</td>
      <td class="text-muted">${esc(`${m.stageCount} × ${m.stageNoun}`)}<div class="am-sub">${esc(rhythmText(m.rhythmMonths))}</div></td>
      <td>${lehrplanSummary(m)}</td>
      <td class="text-muted">${m.classCount}</td>
      <td class="am-chevron">›</td>
    </tr>`).join("");
}

function openForm(model?: CourseModel) {
  const isEdit = !!model;
  const readOnly = !canWrite();
  const disabled = readOnly ? " disabled" : "";

  const current = {
    stageNoun: model?.stageNoun || "Ausbildungsjahr",
    rhythmMonths: model?.rhythmMonths ?? 12,
    stageCount: model?.stageCount ?? 3,
    stageLabels: model?.stageLabels || [],
  };

  const currentKey = model?.groupKey || "";
  const programOpts = `<option value="">Alle Ausbildungsgänge</option>` +
    allPrograms().map(p => {
      const suffix = p.classCount > 0 ? ` (${p.classCount} Klasse${p.classCount === 1 ? "" : "n"})` : "";
      return `<option value="${esc(p.key)}" ${currentKey === p.key ? "selected" : ""}>${esc(p.name)}${suffix}</option>`;
    }).join("");

  const rhythmOpts = RHYTHM_OPTIONS
    .map(o => `<option value="${o.months}" ${current.rhythmMonths === o.months ? "selected" : ""}>${o.label}</option>`)
    .join("");

  openModal(isEdit ? (readOnly ? "Ausbildungsmodell" : "Ausbildungsmodell bearbeiten") : "Neues Ausbildungsmodell", `
    <div class="form-grid">
      <div class="form-group">
        <label>Name *</label>
        <input id="m-name" value="${esc(model?.name || "")}"${disabled} placeholder="z.B. Physiotherapie Vollzeit" />
      </div>
      <div class="form-group">
        <label>Ausbildungsgang</label>
        <select id="m-program"${disabled}>${programOpts}</select>
        <small class="text-muted">Bestimmt, an welchen Klassen dieses Modell zur Auswahl steht.</small>
      </div>
    </div>
    <div class="form-grid">
      <div class="form-group">
        <label>Eine Stufe heißt</label>
        <input id="m-noun" value="${esc(current.stageNoun)}"${disabled} placeholder="Ausbildungsjahr" />
      </div>
      <div class="form-group">
        <label>Nächste Stufe startet</label>
        <select id="m-rhythm"${disabled}>${rhythmOpts}</select>
      </div>
    </div>
    <div class="form-group">
      <label>Anzahl der Stufen</label>
      <input type="number" min="1" max="20" id="m-count" value="${current.stageCount}"${disabled} />
    </div>
    <div class="form-group">
      <label>Eigene Namen je Stufe (optional)</label>
      <small class="text-muted">Leer lassen, um durchzunummerieren.</small>
      <div id="m-labels" style="display:flex;flex-direction:column;gap:.375rem;margin-top:.375rem"></div>
    </div>
    <div class="alert alert-info" id="m-preview" style="font-size:.8125rem"></div>
    <div class="alert alert-error hidden" id="m-error"></div>
    <div class="form-actions">
      ${isEdit ? writeOnly(`<button type="button" class="lp-danger-link" id="m-delete">Modell löschen</button>`) : ""}
      <button class="btn btn-secondary" id="m-cancel">${readOnly ? "Schließen" : "Abbrechen"}</button>
      ${writeOnly(`<button class="btn btn-primary" id="m-save">Speichern</button>`)}
    </div>`, { wide: true });

  const mb = modalBody();
  const nounInput = mb.querySelector("#m-noun") as HTMLInputElement;
  const countInput = mb.querySelector("#m-count") as HTMLInputElement;
  const labelsBox = mb.querySelector("#m-labels") as HTMLElement;
  const preview = mb.querySelector("#m-preview") as HTMLElement;

  /** Aktuell eingetragene Stufennamen, damit Tippen beim Umbauen nicht verloren geht. */
  const readLabels = (): string[] =>
    [...labelsBox.querySelectorAll<HTMLInputElement>("input")].map(i => i.value);

  const renderLabels = (values: string[]) => {
    const count = Math.min(Math.max(parseInt(countInput.value) || 1, 1), 20);
    labelsBox.innerHTML = Array.from({ length: count }, (_, i) => {
      const noun = nounInput.value.trim() || "Stufe";
      return `<input id="m-label-${i}" value="${esc(values[i] || "")}"${disabled} placeholder="${esc(`${i + 1}. ${noun}`)}" />`;
    }).join("");
  };

  const updatePreview = () => {
    const count = Math.min(Math.max(parseInt(countInput.value) || 1, 1), 20);
    const source = { stageNoun: nounInput.value.trim() || "Stufe", stageLabels: readLabels(), stageCount: count };
    preview.textContent = `Stufen: ${stagePreview(source)}`;
  };

  renderLabels(current.stageLabels);
  updatePreview();

  mb.querySelector("#m-cancel")!.addEventListener("click", closeModal);
  if (readOnly) { lockInputs(mb); return; }

  mb.querySelector("#m-delete")?.addEventListener("click", async () => {
    // Klassen behalten ihren Kursbeginn, verlieren aber die Zuordnung und damit den
    // berechneten Jahrgang. Mit dem Modell geht sein Lehrplan. Beides gehört in die Rückfrage.
    const hint = model!.classCount > 0
      ? ` ${model!.classCount} Klasse${model!.classCount === 1 ? "" : "n"} verliert dadurch den Jahrgang, der Kursbeginn bleibt erhalten.`
      : "";
    const lp = model!.curriculum.subjectCount > 0 ? ` Der Lehrplan mit ${model!.curriculum.subjectCount} Fächern wird ebenfalls gelöscht, die Fächer selbst bleiben.` : "";
    const ok = await confirmDialog({
      title: "Ausbildungsmodell löschen",
      message: `„${model!.name}“ wird gelöscht.${hint}${lp}`,
      confirmLabel: "Löschen", danger: true,
    });
    if (!ok) return;
    try {
      await api.stundenplan.courseModels.delete(model!.id);
      closeModal();
      showToast("Ausbildungsmodell gelöscht");
      navigate(null);
    } catch (err: any) { showError(mb, err.message); }
  });

  countInput.addEventListener("input", () => { renderLabels(readLabels()); updatePreview(); });
  nounInput.addEventListener("input", () => { renderLabels(readLabels()); updatePreview(); });
  labelsBox.addEventListener("input", updatePreview);

  mb.querySelector("#m-save")!.addEventListener("click", async () => {
    const key = (mb.querySelector("#m-program") as HTMLSelectElement).value;
    const data = {
      name: (mb.querySelector("#m-name") as HTMLInputElement).value.trim(),
      departmentGroupId: key.startsWith("p-") ? +key.slice(2) : null,
      customGroupId: key.startsWith("c-") ? +key.slice(2) : null,
      stageNoun: nounInput.value.trim() || "Ausbildungsjahr",
      rhythmMonths: +(mb.querySelector("#m-rhythm") as HTMLSelectElement).value,
      stageCount: parseInt(countInput.value) || 1,
      stageLabels: readLabels(),
    };
    if (!data.name) {
      showError(mb, "Name ist erforderlich");
      return;
    }

    const saveBtn = mb.querySelector("#m-save") as HTMLButtonElement;
    saveBtn.disabled = true; saveBtn.textContent = "Speichern…";
    try {
      const saved = isEdit
        ? await api.stundenplan.courseModels.update(model!.id, data)
        : await api.stundenplan.courseModels.create(data);
      closeModal();
      showToast(isEdit ? "Ausbildungsmodell gespeichert" : "Ausbildungsmodell angelegt");
      // Ein neues Modell gleich öffnen: als Nächstes kommt sein Lehrplan.
      navigate(saved.id);
    } catch (err: any) {
      showError(mb, err.message);
      saveBtn.disabled = false; saveBtn.textContent = "Speichern";
    }
  });

  (mb.querySelector("#m-name") as HTMLInputElement).focus();
}

async function load() {
  [models, programs] = await Promise.all([
    api.stundenplan.courseModels.list(),
    api.stundenplan.programs(),
  ]);
}

export async function render(el: HTMLElement) {
  rootEl = el;
  await show(el);
}

/** Liste oder Modellseite, je nach URL. */
async function show(el: HTMLElement) {
  el.innerHTML = `<div style="display:flex;align-items:center;justify-content:center;height:200px"><div class="spinner"></div></div>`;
  try {
    await load();
  } catch (err: any) {
    el.innerHTML = `<div class="alert alert-error">Fehler: ${esc(err.message)}</div>`;
    return;
  }
  const id = modelFromUrl();
  const model = id !== null ? models.find(m => m.id === id) : undefined;
  if (model) await renderDetail(el, model);
  else renderList(el);
}

function renderList(el: HTMLElement) {
  el.innerHTML = `
    <div class="am-page">
      <div class="settings-page-header">
        <div>
          <h1>Ausbildungsmodelle</h1>
          <p>Jedes Modell legt Takt und Stufen fest und enthält einen Lehrplan: welche Fächer in welcher Stufe mit wie vielen Unterrichtseinheiten laufen. An der Klasse wird ein Modell ausgewählt.</p>
        </div>
        ${writeOnly(`<button class="btn btn-primary" id="btn-add">+ Neues Modell</button>`)}
      </div>
      ${readOnlyNotice()}
      <div class="card table-wrap">
        <table>
          <thead><tr>
            <th>Name</th>
            <th style="width:180px">Ausbildungsgang</th>
            <th style="width:240px">Stufen</th>
            <th style="width:190px">Lehrplan</th>
            <th style="width:90px">Klassen</th>
            <th style="width:40px"></th>
          </tr></thead>
          <tbody></tbody>
        </table>
      </div>
    </div>`;
  currentEl = el.querySelector(".am-page") as HTMLElement;
  renderTable();

  currentEl.querySelector("#btn-add")?.addEventListener("click", () => openForm());
  currentEl.querySelector("tbody")!.addEventListener("click", (e) => {
    const row = (e.target as HTMLElement).closest("[data-open]") as HTMLElement | null;
    if (row) navigate(+row.dataset.open!);
  });
}

async function renderDetail(el: HTMLElement, model: CourseModel) {
  el.innerHTML = `
    <div class="am-page">
      <button type="button" class="am-back" id="am-back">‹ Alle Ausbildungsmodelle</button>
      <div class="am-head">
        <div class="am-title">
          <h1 title="${esc(model.name)}">${esc(model.name)}</h1>
          ${model.active ? "" : `<span class="badge badge-gray">inaktiv</span>`}
          <button type="button" class="btn btn-secondary btn-square" id="am-edit"
            title="${canWrite() ? "Ausbildungsmodell bearbeiten" : "Details"}" aria-label="${canWrite() ? "Ausbildungsmodell bearbeiten" : "Details"}">${canWrite() ? Icons.pencilSquare : Icons.eye}</button>
        </div>
        <div class="settings-page-actions am-actions" id="am-actions"></div>
      </div>
      ${readOnlyNotice()}
      <div id="am-lehrplan"></div>
    </div>`;
  currentEl = el.querySelector(".am-page") as HTMLElement;
  currentEl.querySelector("#am-back")!.addEventListener("click", () => navigate(null));
  currentEl.querySelector("#am-edit")!.addEventListener("click", () => openForm(model));
  // Die Knöpfe des Lehrplans stehen oben rechts im Seitenkopf, wie überall in der Anwendung.
  await renderCurriculumEditor(currentEl.querySelector("#am-lehrplan") as HTMLElement, model, models, async () => {
    // Kennzahlen für „Kopieren von …“ und die Liste aktuell halten.
    const fresh = await api.stundenplan.courseModels.list().catch(() => null);
    if (fresh) { models.splice(0, models.length, ...fresh); }
  }, currentEl.querySelector("#am-actions") as HTMLElement);
}

function showError(mb: HTMLElement, message: string) {
  const box = mb.querySelector("#m-error") as HTMLElement | null;
  if (!box) { showToast(message); return; }
  box.textContent = message;
  box.classList.remove("hidden");
}
